import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
} from './Table';
import Button from './Button';
import { Pencil, Trash2 } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import EmptyState from './EmptyState';

interface Column<T> {
  header: string;
  accessorKey: keyof T;
  cell?: (row: T) => React.ReactNode;
  className?: string;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  isLoading?: boolean;
  emptyStateProps?: {
    icon?: React.ReactNode;
    title?: string;
    description?: string;
    action?: {
      label: string;
      onClick: () => void;
    };
  };
}

function DataTable<T>({
  columns,
  data,
  onEdit,
  onDelete,
  isLoading,
  emptyStateProps,
}: DataTableProps<T>) {
  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (data.length === 0) {
    if (emptyStateProps) {
      return (
        <EmptyState
          icon={emptyStateProps.icon}
          title={emptyStateProps.title || 'Nenhum dado encontrado'}
          description={emptyStateProps.description}
          action={emptyStateProps.action}
        />
      );
    }

    return <div className="text-center py-8 text-gray-500">Nenhum dado encontrado</div>;
  }

  return (
    <TableContainer>
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map(column => (
              <TableHead key={String(column.accessorKey)} className={column.className}>
                {column.header}
              </TableHead>
            ))}
            {(onEdit || onDelete) && (
              <TableHead className="relative whitespace-nowrap">
                <span className="sr-only">Ações</span>
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((row, i) => (
            <TableRow key={i}>
              {columns.map(column => (
                <TableCell key={String(column.accessorKey)} className={column.className}>
                  {column.cell ? column.cell(row) : String(row[column.accessorKey] || '')}
                </TableCell>
              ))}
              {(onEdit || onDelete) && (
                <TableCell className="text-right space-x-2 whitespace-nowrap">
                  {onEdit && (
                    <Button variant="ghost" onClick={() => onEdit(row)} title="Editar">
                      <Pencil className="h-4 w-4 text-primary-600" />
                    </Button>
                  )}
                  {onDelete && (
                    <Button variant="ghost" onClick={() => onDelete(row)} title="Excluir">
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default DataTable;
